import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const departmentInfo: Record<string, { name: string; nameAr: string; description: string; descriptionAr: string; icon: string }> = {
  'electronics': {
    name: 'ALEBEL Electronics',
    nameAr: 'إلكترونيات الأبل',
    description: 'Investment in electronics product development, embedded systems, IoT devices, and RF communication solutions for smart control and monitoring applications.',
    descriptionAr: 'استثمار في تطوير المنتجات الإلكترونية والأنظمة المدمجة وأجهزة إنترنت الأشياء وحلول الاتصالات اللاسلكية لتطبيقات التحكم الذكي والمراقبة.',
    icon: 'ri-cpu-line',
  },
  'construction': {
    name: 'TOP TEAM Construction',
    nameAr: 'توب تيم للإنشاءات',
    description: 'A strategic portfolio company specializing in construction, infrastructure development, and turnkey building projects.',
    descriptionAr: 'شركة محفظة استراتيجية متخصصة في البناء وتطوير البنية التحتية ومشاريع البناء الجاهزة.',
    icon: 'ri-building-2-line',
  },
  'rnd': {
    name: 'ALEBEL R&D',
    nameAr: 'أبحاث وتطوير الأبل',
    description: 'Dedicated research and development division driving innovation across energy, technology, and industrial sectors.',
    descriptionAr: 'قسم متخصص للبحث والتطوير يقود الابتكار عبر قطاعات الطاقة والتكنولوجيا والصناعة.',
    icon: 'ri-flask-line',
  },
  'robotics': {
    name: 'ALEBEL Robotics',
    nameAr: 'روبوتيكس الأبل',
    description: 'Advanced robotics and automation solutions for industrial applications, smart manufacturing, and process optimization.',
    descriptionAr: 'حلول روبوتية وأتمتة متقدمة للتطبيقات الصناعية والتصنيع الذكي وتحسين العمليات.',
    icon: 'ri-robot-line',
  },
  'device': {
    name: 'ALEBEL Device',
    nameAr: 'جهاز الأبل',
    description: 'Smart devices and electronics solutions for consumer and industrial markets, focused on innovation and quality.',
    descriptionAr: 'أجهزة ذكية وحلول إلكترونية للأسواق الاستهلاكية والصناعية، تركز على الابتكار والجودة.',
    icon: 'ri-device-line',
  },
  'tech': {
    name: 'ALEBEL Tech',
    nameAr: 'أبل تك',
    description: 'Developing integrated technology solutions and innovative digital services that support digital transformation and enhance operational efficiency across various sectors.',
    descriptionAr: 'تطوير حلول تقنية متكاملة وخدمات رقمية مبتكرة تدعم التحول الرقمي وتعزز الكفاءة التشغيلية عبر مختلف القطاعات.',
    icon: 'ri-code-s-slash-line',
  },
};

export default function ComingSoonPage() {
  const { department } = useParams<{ department: string }>();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const info = department ? departmentInfo[department] : null;

  const name = isRTL ? (info?.nameAr || 'قريباً') : (info?.name || 'Coming Soon');
  const description = isRTL ? (info?.descriptionAr || 'هذه الصفحة قيد الإنشاء حالياً.') : (info?.description || 'This page is currently under construction.');
  const icon = info?.icon || 'ri-time-line';

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-[#1A3A5C] to-[#0D2137] animate-gradient"></div>
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-gold/10 animate-gradient"></div>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative text-center">
          <div className="w-20 h-20 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <i className={`${icon} text-4xl text-gold`}></i>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight mb-6">
            {name}
          </h1>
          <p className="text-lg md:text-xl text-gray-400 leading-relaxed max-w-2xl mx-auto">
            {description}
          </p>
        </div>
      </section>

      {/* Coming Soon Content */}
      <section className="py-24">
        <div className="max-w-[700px] mx-auto px-4 md:px-8 text-center">

          {/* Coming Soon Badge */}
          <div className="inline-flex items-center gap-3 bg-gold/10 border border-gold/30 rounded-full px-6 py-3 mb-10">
            <div className="w-2 h-2 bg-gold rounded-full animate-pulse"></div>
            <span className="text-gold font-medium tracking-widest uppercase text-sm">
              {isRTL ? 'قريباً' : 'Coming Soon'}
            </span>
          </div>

          <h2 className="font-serif text-3xl md:text-4xl text-navy mb-6">
            {isRTL ? 'نعمل على ذلك' : "We're Working on It"}
          </h2>
          <p className="text-gray-500 text-lg leading-relaxed mb-12">
            {isRTL
              ? 'هذه الصفحة قيد التطوير حالياً. نحن نبني شيئاً رائعاً. ترقبوا التحديثات أو تواصلوا معنا للمزيد من المعلومات.'
              : "This page is currently under development. We're building something great. Stay tuned for updates or contact us for more information."}
          </p>

          {/* Action Buttons */}
          <div className={`flex flex-wrap gap-4 justify-center ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link
              to="/"
              className={`px-8 py-4 bg-gradient-to-r from-gold to-gold-dark text-navy font-semibold rounded-lg hover:shadow-lg hover:shadow-gold/20 transition-all duration-300 flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <i className="ri-home-line text-xl"></i>
              {isRTL ? 'العودة للرئيسية' : 'Back to Home'}
            </Link>
            <Link
              to="/contact"
              className={`px-8 py-4 border border-navy/20 text-navy font-medium rounded-lg hover:bg-navy/5 transition-all duration-300 flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <i className="ri-mail-line text-xl"></i>
              {isRTL ? 'تواصل معنا' : 'Contact Us'}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
